import React, { useState } from "react";
import { useTheme } from "@emotion/react";
import { CircularProgress, Grid, IconButton, Paper, Tab, Tabs, Typography } from "@mui/material";
import { ReactComponent as CloseIcon } from "assets/close_icon.svg";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import ModalComponent from "ui-components/ModalComponent";
import CustomInputField from "ui-components/formik/CustomInputField";
import { handleCopyClick } from "utils/index";
import { Primary } from 'ui-components/Buttons';

let emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const InviteDealflowModal = ({ open, onClose, result, onSend, isLoading }) => {
  const theme = useTheme();
  const [inviteTab, setInviteTab] = useState(0);
  const [copied, setCopied] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: result?.email || "",
      startup_name: result?.startup_name || "",
      message: "",
    },
    validationSchema: Yup.object({
      startup_name: Yup.string().required("Please enter startup name"),
      email: Yup.string()
        .required("Please enter email")
        .test("email", "Invalid email address", (value) => emailPattern.test(value)),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const payload = {
        email: String(values?.email)?.toLowerCase()?.trim(),
        startup_name: values?.startup_name?.trim(),
        message: values?.message?.trim(),
      };
      await onSend({ ...result, ...payload });
    },
  });

  if (!result) return null;

  const LinkBox = ({ value, isCopied, onCopy }) => (
    <Grid item xs={12}>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{ textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.75, display: "block" }}
      >
        Link
      </Typography>
      <Grid
        container
        alignItems="center"
        gap={1}
        sx={{
          bgcolor: theme.palette.grey[50],
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "8px",
          px: { xs: 1, sm: 1.5 },
          py: 1.25,
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontFamily: "monospace",
            fontSize: { xs: "0.7rem", sm: "0.8rem" },
          }}
        >
          {value}
        </Typography>
        <IconButton
          size="small"
          onClick={() => handleCopyClick(value, isCopied, onCopy)}
          sx={{
            bgcolor: isCopied ? "success.main" : theme.palette.customColors.b300,
            color: "#fff",
            borderRadius: "6px",
            px: 1.5,
            py: 0.5,
            "&:hover": {
              bgcolor: isCopied ? "success.dark" : theme.palette.customColors.b300,
              opacity: 0.9,
            },
            fontSize: "0.78rem",
            fontWeight: 600,
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          {isCopied ? "✓ Copied" : "Copy"}
        </IconButton>
      </Grid>
    </Grid>
  );

  return (
    <ModalComponent open={open} onClose={onClose} style={{ maxWidth: 580, width: "calc(100% - 1rem)" }}>
      <Paper sx={{ background: theme.palette.customColors.whiteBG, borderRadius: "12px", overflow: "hidden" }}>
        <IconButton sx={{ position: "absolute", top: { xs: 12, sm: 16 }, right: { xs: 12, sm: 16 }, padding: 0, zIndex: 1 }}>
          <CloseIcon
            style={{
              width: { xs: 28, sm: 36 },
              height: { xs: 28, sm: 36 },
              color: theme.palette.customColors.g300,
            }}
            onClick={onClose}
          />
        </IconButton>

        {/* Header */}
        <Grid sx={{ px: { xs: "1rem", sm: "1.5rem" }, pt: { xs: "1rem", sm: "1.5rem" }, pb: "1rem", borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" }, fontWeight: 700, color: theme.palette.customColors.g300, lineHeight: 1.3 }}>
            Invite Startup
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Send a direct email invitation.
          </Typography>
        </Grid>

        {/* Tab bar */}
        {/* <Tabs
          value={inviteTab}
          onChange={(_, v) => setInviteTab(v)}
          variant="fullWidth"
          sx={{
            px: { xs: "1rem", sm: "1.5rem" },
            "& .MuiTab-root": { textTransform: "none", fontSize: { xs: "0.75rem", sm: "0.82rem" }, fontWeight: 600, minHeight: 44, py: 1 },
            "& .MuiTabs-indicator": { height: 3, borderRadius: 2 },
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Tab label="Copy Link" />
          <Tab label="Email" />
        </Tabs> */}

        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) e.preventDefault(); }}>
            <Grid display="grid" container sx={{ px: { xs: "1rem", sm: "1.5rem" }, py: { xs: "1rem", sm: "1.25rem" }, gap: { xs: "0.75rem", sm: "1rem" } }}>

              {/* Tab 0: Copy Link */}
              {/* {inviteTab === 0 && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.85rem", sm: "1rem" }, mb: 1.5 }}>
                      Share this link to directly invite {result.startup_name} to your deal flow.
                    </Typography>
                  </Grid>
                  <LinkBox value={`Startup: ${result.startup_name}`} isCopied={copied} onCopy={setCopied} />
                </>
              )} */}

              {/* Tab 1: Email */}
              {/* {inviteTab === 1 && ( */}
              <>
                {/* <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.85rem", sm: "1rem" }, mb: 0.5 }}>
                      Send a direct email invitation. The startup will receive a personalised invite link.
                    </Typography>
                  </Grid> */}
                <Grid item xs={12}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75, display: 'block' }}>
                      Startup Name
                    </Typography>
                    <CustomInputField
                      name="startup_name"
                      // label="Startup Name"
                      placeholder="Enter startup name"
                      value={formik?.values?.startup_name}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 30 && /^[^@$()]*$/.test(value)) {
                          formik.setFieldValue("startup_name", value.charAt(0).toUpperCase() + value.slice(1));
                        }
                      }}
                      tabIndex={1}
                      InputProps={{
                        sx: {
                          border: `1px solid ${theme.palette.divider}`, borderRadius: '8px',
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75, display: 'block' }}>
                      Founder Email
                    </Typography>
                    <CustomInputField
                      name="email"
                      // label="Founder Email"
                      placeholder="founder@startup.com"
                      value={formik?.values?.email}
                      onChange={(e) => formik.setFieldValue("email", String(e.target.value)?.toLowerCase())}
                      tabIndex={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Primary
                      type="submit"
                      fullWidth
                      sx={{ height: { xs: 44, sm: 52 }, fontSize: { xs: "0.95rem", sm: "1rem" } }}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <CircularProgress sx={{ color: "white", marginInline: "auto" }} size={"1.2rem"} />
                      ) : (
                        "Send Invitation"
                      )}
                    </Primary>
                  </Grid>
                </Grid>

              </>
              {/* )} */}
            </Grid>
          </form>
        </FormikProvider>
      </Paper>
    </ModalComponent>
  );
};

export default InviteDealflowModal;
